import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../../utils/axiosConfig';

// Inscribir equipo (General - redirige a MP)
export const createEnrollmentPreference = createAsyncThunk(
    'teams/createEnrollmentPreference',
    async ({ tournamentId, teamName }, { rejectWithValue }) => {
        try {
            const response = await axios.post(`/api/teams/create-enrollment-preference`, {
                tournamentId,
                teamName
            });
            return response.data; // { preferenceId, initPoint }
        } catch (error) {
            return rejectWithValue(error.response?.data || { error: error.message });
        }
    }
);

// Obtener equipos de un torneo (Admin / Público)
export const fetchTeams = createAsyncThunk(
    'teams/fetchTeams',
    async (tournamentId, { rejectWithValue }) => {
        try {
            const response = await axios.get(`/api/teams/${tournamentId}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { error: error.message });
        }
    }
);

// Obtener mis equipos para un torneo (Capitán)
export const fetchMyTeams = createAsyncThunk(
    'teams/fetchMyTeams',
    async (tournamentId, { rejectWithValue }) => {
        try {
            const response = await axios.get(`/api/teams/my-teams/${tournamentId}`);
            return response.data;
        } catch (error) {
            // Si es 404/401, lo manejamos suavemente
            return rejectWithValue(error.response?.data || { error: error.message });
        }
    }
);

// Actualizar jugadores (Capitán o Admin)
export const updateTeamPlayers = createAsyncThunk(
    'teams/updateTeamPlayers',
    async ({ teamId, players }, { rejectWithValue }) => {
        try {
            const response = await axios.put(`/api/teams/${teamId}/players`, { players });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { error: error.message });
        }
    }
);

const teamSlice = createSlice({
    name: 'teams',
    initialState: {
        teams: [],         // Equipos inscriptos en el torneo activo
        myTeams: [],       // Mis equipos en el torneo activo
        loading: false,
        error: null,
        preferenceUrl: null, // MP checkout
    },
    reducers: {
        clearPreferenceUrl: (state) => {
            state.preferenceUrl = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // Enrollment
            .addCase(createEnrollmentPreference.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.preferenceUrl = null;
            })
            .addCase(createEnrollmentPreference.fulfilled, (state, action) => {
                state.loading = false;
                state.preferenceUrl = action.payload.initPoint;
            })
            .addCase(createEnrollmentPreference.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // Fetch Teams
            .addCase(fetchTeams.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchTeams.fulfilled, (state, action) => {
                state.loading = false;
                state.teams = action.payload;
            })
            .addCase(fetchTeams.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // Fetch My Teams
            .addCase(fetchMyTeams.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchMyTeams.fulfilled, (state, action) => {
                state.loading = false;
                state.myTeams = action.payload;
            })
            .addCase(fetchMyTeams.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // Update Players
            .addCase(updateTeamPlayers.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateTeamPlayers.fulfilled, (state, action) => {
                state.loading = false;

                // Actualizar dentro de mis equipos (myTeams)
                const myIdx = state.myTeams.findIndex(t => t._id === action.payload._id);
                if (myIdx !== -1) {
                    state.myTeams[myIdx] = action.payload;
                }

                // Si estoy admin, también actualizo la lista general
                const idx = state.teams.findIndex(t => t._id === action.payload._id);
                if (idx !== -1) {
                    state.teams[idx] = action.payload;
                }
            })
            .addCase(updateTeamPlayers.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

export const { clearPreferenceUrl } = teamSlice.actions;
export default teamSlice.reducer;
